/**
 * Widget Settings Page
 * Ermöglicht das Hinzufügen, Aktivieren/Deaktivieren und Konfigurieren von Widgets
 */
import React, { useEffect, useState } from 'react';
import { BsGearFill } from "react-icons/bs";
import { toast } from 'react-toastify';
import { widgetRegistry } from '../widgets/registry';
import { settingsStorage } from '../settings/storage';
import SizeSettings from '../widgets/components/SizeSettings';
import './WidgetSettingsPage.scss';

const WidgetSettingsPage = ({ changeTitle }) => {
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [widgetConfigs, setWidgetConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWidgetType, setSelectedWidgetType] = useState('');
  const [newWidgetTitle, setNewWidgetTitle] = useState('');
  const [editingWidgetId, setEditingWidgetId] = useState(null);

  // Lade verfügbare Widgets und Konfigurationen beim Start
  useEffect(() => {
    // Setze den Titel der Seite
    if (changeTitle) {
      changeTitle("Widget Settings");
    }

    const loadData = async () => {
      try {
        // Synchronisiere Einstellungen mit dem Backend
        await settingsStorage.sync();
        
        // Hole alle registrierten Widgets
        const widgets = widgetRegistry.getAllWidgets();
        setAvailableWidgets(widgets);
        
        // Hole alle Widget-Konfigurationen (leer lassen, bis Benutzer Widgets hinzufügt)
        const configs = settingsStorage.getAllConfigs();
        setWidgetConfigs(configs);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading widget data:', err);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Fügt ein neues Widget hinzu
  const handleAddWidget = () => {
    if (!selectedWidgetType) {
      alert('Bitte wählen Sie einen Widget-Typ aus.');
      return;
    }

    const widgetDefinition = widgetRegistry.getWidget(selectedWidgetType);
    if (!widgetDefinition) {
      alert('Ungültiger Widget-Typ ausgewählt.');
      return;
    }

    // Erstelle eine neue Widget-Instanz mit Standardkonfiguration
    const newId = `widget-${Date.now()}`; // In Produktion sollte eine UUID verwendet werden
    
    // Verwende die Standardkonfiguration des Widgets
    const newInstance = {
      id: newId,
      type: selectedWidgetType,
      title: newWidgetTitle.trim() || widgetDefinition.defaultConfig.title || widgetDefinition.name,
      isActive: widgetDefinition.defaultConfig.isActive !== undefined ? widgetDefinition.defaultConfig.isActive : true,
      position: widgetConfigs.length, // Setze Position auf Ende der Liste
      size: widgetDefinition.defaultConfig.size || { width: 1, height: 1 },
      globalSettings: widgetDefinition.defaultConfig.globalSettings || {},
      ...(widgetDefinition.defaultConfig.localSettings && {
        localSettings: widgetDefinition.defaultConfig.localSettings
      })
    };
    
    // Speichere im Storage
    settingsStorage.setConfig(newInstance);
    
    // Aktualisiere lokalen State
    setWidgetConfigs([...widgetConfigs, newInstance]);
    
    // Setze Formular zurück
    setSelectedWidgetType('');
    setNewWidgetTitle('');
    
    // Optional: Speichere im Backend
    // settingsStorage.saveToBackend([...widgetConfigs, newInstance]);
  };

  // Aktiviert/Deaktiviert ein Widget
  const toggleWidgetActive = (id) => {
    const config = widgetConfigs.find(c => c.id === id);
    if (config) {
      const updatedConfig = { ...config, isActive: !config.isActive };
      settingsStorage.updateConfig(id, updatedConfig);
      setWidgetConfigs(widgetConfigs.map(c => c.id === id ? updatedConfig : c));
    }
  };

  // Aktualisiert die Position eines Widgets
  const updateWidgetPosition = (id, position) => {
    const config = widgetConfigs.find(c => c.id === id);
    if (config) {
      const updatedConfig = { ...config, position };
      settingsStorage.updateConfig(id, updatedConfig);
      setWidgetConfigs(widgetConfigs.map(c => c.id === id ? updatedConfig : c));
    }
  };

  // Aktualisiert die Größe eines Widgets
  const updateWidgetSize = (id, size) => {
    const config = widgetConfigs.find(c => c.id === id);
    if (config) {
      const updatedConfig = { ...config, size };
      settingsStorage.updateConfig(id, updatedConfig);
      setWidgetConfigs(widgetConfigs.map(c => c.id === id ? updatedConfig : c));
    }
  };

  // Entfernt ein Widget
  const removeWidget = (id) => {
    if (window.confirm('Möchten Sie dieses Widget wirklich entfernen?')) {
      settingsStorage.removeConfig(id);
      setWidgetConfigs(widgetConfigs.filter(c => c.id !== id));
      
      // Wenn das bearbeitete Widget entfernt wird, setze den Bearbeitungsmodus zurück
      if (editingWidgetId === id) {
        setEditingWidgetId(null);
      }
    }
  };

  // Speichert die Einstellungen im Backend
  const saveSettings = async () => {
    try {
      const success = await settingsStorage.saveToBackend(widgetConfigs);
      if (success) {
        toast.success('Einstellungen erfolgreich gespeichert!');
      } else {
        toast.error('Fehler beim Speichern der Einstellungen.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern der Einstellungen.');
    }
  };

  // Handler für Größenänderungen
  const handleSizeChange = (config) => {
    updateWidgetSize(config.id, config.size);
  };

  if (loading) {
    return <div className="widget-settings-loading">Lade Einstellungen...</div>;
  }

  return (
    <div className="widget-settings-page">
      <div className="add-widget-section">
        <h2>Neues Widget hinzufügen</h2>
        <div className="add-widget-form">
          <div className="form-group">
            <label htmlFor="widget-type">Widget-Typ:</label>
            <select
              id="widget-type"
              value={selectedWidgetType}
              onChange={(e) => setSelectedWidgetType(e.target.value)}
            >
              <option value="">Bitte wählen...</option>
              {availableWidgets.map(widget => (
                <option key={widget.type} value={widget.type}>
                  {widget.name} - {widget.description}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="widget-title">Titel (optional):</label>
            <input
              type="text"
              id="widget-title"
              value={newWidgetTitle}
              onChange={(e) => setNewWidgetTitle(e.target.value)}
              placeholder="Widget-Titel"
            />
          </div>
          
          <button onClick={handleAddWidget}>Widget hinzufügen</button>
        </div>
      </div>
      
      <div className="widgets-list">
        <h2>Konfigurierte Widgets</h2>
        {widgetConfigs.length > 0 ? (
          <div className="widgets-table">
            <div className="table-header">
              <div className="header-cell" style={{ minWidth: '20%', width: '20%' }}>Titel</div>
              <div className="header-cell">Typ</div>
              <div className="header-cell" style={{ textAlign: 'center' }}>Aktiv</div>
              <div className="header-cell" style={{ textAlign: 'center' }}>Position</div>
              <div className="header-cell" style={{ textAlign: 'center' }}>Einstellungen</div>
              <div className="header-cell" style={{ textAlign: 'center' }}>Aktionen</div>
            </div>
            
            {widgetConfigs.map(config => {
              const widgetDefinition = widgetRegistry.getWidget(config.type);
              const isEditing = editingWidgetId === config.id;
              
              return (
                <div key={config.id} className="table-row">
                  <div className="cell" style={{ minWidth: '20%', width: '20%' }}>{config.title}</div>
                  <div className="cell">{widgetDefinition?.name || config.type}</div>
                  <div className="cell" style={{ textAlign: 'center', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      checked={config.isActive}
                      onChange={() => toggleWidgetActive(config.id)}
                    />
                  </div>
                  <div className="cell" style={{ textAlign: 'center', justifyContent: 'center' }}>
                    <input
                      type="number"
                      value={config.position}
                      onChange={(e) => updateWidgetPosition(config.id, parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  <div className="cell" style={{ textAlign: 'center', justifyContent: 'center' }}>
                    <button 
                      onClick={() => setEditingWidgetId(isEditing ? null : config.id)}
                      className="edit-button"
                    >
                      {isEditing ? 'Schließen' : <BsGearFill />}
                    </button>
                  </div>
                  <div className="cell actions" style={{ textAlign: 'center', justifyContent: 'center' }}>
                    <button 
                      onClick={() => removeWidget(config.id)}
                      className="danger"
                    >
                      Entfernen
                    </button>
                  </div>
                  
                  {isEditing && (
                    <div className="widget-settings-expanded">
                      <div className="settings-section">
                        <SizeSettings 
                          config={config}
                          onChange={handleSizeChange}
                        />
                      </div>
                      
                      {widgetDefinition?.settingsComponent && (
                        <div className="settings-section">
                          {React.createElement(widgetDefinition.settingsComponent, {
                            config,
                            onChange: (updatedConfig) => {
                              settingsStorage.updateConfig(config.id, updatedConfig);
                              setWidgetConfigs(widgetConfigs.map(c => 
                                c.id === config.id ? updatedConfig : c
                              ));
                            }
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p>Keine Widgets konfiguriert.</p>
        )}
      </div>
      
      <div className="settings-actions">
        <button onClick={saveSettings}>Einstellungen speichern</button>
      </div>
    </div>
  );
};

export default WidgetSettingsPage;
